import * as React from "react";

import { DataExtractor, getExtractor } from "../../utils/dataView";
import { customOrDefaultFilterTransformer } from "../../utils/dataView/extractor";
import { t } from "../../utils/i18n";
import {
    SimpleAndFilter,
    SimpleFieldFilter,
    QueryResult,
    MatchResult,
    DataViewIndex,
    MATCH_TYPE_EXACT_IGNORE_CASE,
    MATCH_TYPE_WILDCARD,
} from "../../utils/indexing";
import { Set } from "../../utils/set";
import {
    InputWithTypeahead,
    TypeaheadOptionOrGroup,
    TypeaheadOption,
} from "../typeahead";

import * as theme from "./theme.css";

interface ParsedCriterion {
    criterion: string;
    excluded: boolean;
}

interface FilterTypeaheadOption<ITEM> {
    extractor: DataExtractor<ITEM, unknown>;
    filter: SimpleFieldFilter;
    excluded: boolean;
}

const MAX_SUBMATCH_COUNT = 3;

class FilterTypeahead<ITEM> extends React.Component<
    FilterTypeaheadProps<ITEM>,
    FilterTypeaheadState
> {
    constructor(props: FilterTypeaheadProps<ITEM>) {
        super(props);
        this.state = {
            criterion: "",
            criterionRegexp: undefined,
        };
    }
    render() {
        const { id, queryResult, hideResultCount } = this.props;
        const resultCount = hideResultCount
            ? undefined
            : queryResult.dataView.dataSource.iter().length();
        return (
            <div className={theme.search}>
                <InputWithTypeahead<FilterTypeaheadOption<ITEM>>
                    id={id}
                    selectSize="large"
                    value={undefined}
                    formatOption={this.formatOption}
                    onSearch={this.onFilterStringChanged}
                    onSelect={this.onSelectItem}
                    noResultsMessage={t("ControlResult_Filter_NoResult")}
                    className={theme.fullWidthContainer}
                    addonAfter={
                        resultCount !== undefined && resultCount !== null ? (
                            <span className={theme.searchResultsCount}>
                                {t("Result", { count: resultCount })}
                            </span>
                        ) : undefined
                    }
                />
            </div>
        );
    }
    private buildOptionsForOrResult(
        matchResult: MatchResult,
        criterion: ParsedCriterion
    ) {
        const { dataViewIndex } = this.props;
        const options: Array<
            TypeaheadOptionOrGroup<FilterTypeaheadOption<ITEM>>
        > = [];
        dataViewIndex.splitter.split(criterion.criterion, () => {});
        const originalQueryResult = this.props.queryResult;
        const { extractorId } = matchResult; // The labels of the match groups are sorted by relevance
        const extractor = getExtractor(
            originalQueryResult.dataView,
            extractorId
        );
        const otherOrResults = originalQueryResult.orResults.filter(
            (result) => result.extractorId !== extractorId
        );

        const { excludedItemIds } = originalQueryResult;

        // The intersection of itemIds of all the other AND extractors
        let itemIdsFromOtherFilters: Set | null;
        if (otherOrResults.length > 0) {
            itemIdsFromOtherFilters = {};
            otherOrResults.forEach(({ matchGroups }) => {
                matchGroups.forEach((matchGroup) => {
                    matchGroup.itemIds.forEach((itemId) => {
                        itemIdsFromOtherFilters![itemId] = true;
                    });
                });
            });
        } else {
            itemIdsFromOtherFilters = null;
        }

        const groupOptions: Array<
            TypeaheadOption<FilterTypeaheadOption<ITEM>>
        > = [];

        let addedItemsCount = 0;

        const extractorGroups = matchResult.matchGroups;
        let hasAtLeastOneMatch = false;
        for (const matchGroup of extractorGroups) {
            let groupHasItems: boolean;
            if (excludedItemIds === null && itemIdsFromOtherFilters === null) {
                groupHasItems = true;
            } else {
                groupHasItems = matchGroup.itemIds.some((itemId) => {
                    if (excludedItemIds && excludedItemIds.has(itemId)) {
                        return false;
                    }
                    return (
                        itemIdsFromOtherFilters === null ||
                        itemIdsFromOtherFilters[itemId] === true
                    );
                });
            }
            if (groupHasItems) {
                const { value } = matchGroup;
                hasAtLeastOneMatch = true;
                const option: TypeaheadOption<FilterTypeaheadOption<ITEM>> = {
                    type: "Option",
                    value: {
                        extractor,
                        filter: dataViewIndex.toTechnicalFilter(extractorId, {
                            value,
                            matchType: MATCH_TYPE_EXACT_IGNORE_CASE,
                        }),
                        excluded: criterion.excluded,
                    },
                };
                groupOptions.push(option);
                addedItemsCount++;
                if (addedItemsCount >= MAX_SUBMATCH_COUNT) {
                    break;
                }
            }
        }
        // We don't handle multi-fragment wildcards in a good way right now, don't suggest them
        if (
            criterion.criterion !== "" &&
            matchResult.wildcardMatchGroup.itemIds.length > 0
        ) {
            let groupHasItems: boolean;
            if (excludedItemIds === null && itemIdsFromOtherFilters === null) {
                groupHasItems = true;
            } else {
                const matchGroup = matchResult.wildcardMatchGroup;
                groupHasItems = matchGroup.itemIds.some((itemId) => {
                    if (excludedItemIds && excludedItemIds.has(itemId)) {
                        return false;
                    }
                    return (
                        itemIdsFromOtherFilters === null ||
                        itemIdsFromOtherFilters[itemId] === true
                    );
                });
            }
            if (groupHasItems) {
                hasAtLeastOneMatch = true;
                const wildCardOption: TypeaheadOption<
                    FilterTypeaheadOption<ITEM>
                > = {
                    type: "Option",
                    value: {
                        extractor,
                        filter: {
                            value: criterion.criterion,
                            matchType: MATCH_TYPE_WILDCARD,
                        },
                        excluded: criterion.excluded,
                    },
                };
                groupOptions.push(wildCardOption);
            }
        }
        if (!hasAtLeastOneMatch) {
            return [];
        }
        options.push({
            type: "Group",
            value: <div className={theme.optionHeader}>{extractor.label}</div>,
            options: groupOptions,
        });
        return options;
    }
    private formatOption = <FV,>(option: FilterTypeaheadOption<ITEM>) => {
        const { dataViewIndex } = this.props;
        const { criterionRegexp } = this.state;
        // We're displaying suggestions, so transform the option filter to a display filter.
        const filter = dataViewIndex.toDisplayFilter(
            option.extractor.id,
            option.filter
        ) as SimpleFieldFilter<FV>;
        const filterTransformer = customOrDefaultFilterTransformer(
            option.extractor as DataExtractor<ITEM, any, FV>
        );
        let label: React.ReactNode;
        switch (filter.matchType) {
            case MATCH_TYPE_WILDCARD:
                // NOTE: in case of MATCH_TYPE_WILDCARD `filter.value` is always a string.
                const wildcardFilterValue = filter.value as unknown as string;
                label = (
                    <span>
                        <>
                            {t("ControlResult_Filter_Containing")}{" "}
                            <strong>{wildcardFilterValue}</strong>
                        </>
                    </span>
                );
                break;
            default:
                label = (
                    <span>
                        {this.highlight(
                            filterTransformer.asString(filter.value),
                            criterionRegexp
                        )}
                    </span>
                );
                break;
        }
        return label;
    };
    private highlight(value: string, criterionRegexp: RegExp | undefined) {
        const { dataViewIndex } = this.props;
        let nodes: Array<React.ReactNode> = [];
        let key = 0;
        if (criterionRegexp) {
            nodes = [];
            dataViewIndex.splitter.split(value, (fragment, isSeparator) => {
                if (isSeparator) {
                    nodes.push(fragment);
                } else {
                    // criterionRegexp can contain a g flag, so match and exec may have different behaviours
                    const matches = fragment.match(criterionRegexp);
                    if (matches) {
                        const matched = matches[0];
                        nodes.push(<strong key={key++}>{matched}</strong>);
                        nodes.push(fragment.slice(matched.length));
                    } else {
                        nodes.push(fragment);
                    }
                }
            });
            return nodes;
        } else {
            nodes = [value];
        }
        return nodes;
    }
    private onFilterStringChanged = (
        criterion: string,
        callback: (
            options: Array<TypeaheadOptionOrGroup<FilterTypeaheadOption<ITEM>>>
        ) => void
    ) => {
        const { extractorRefs, dataViewIndex } = this.props;
        let criterionRegexp: RegExp | undefined;
        if (criterion) {
            const fragments: Array<string> = [];
            dataViewIndex.splitter.split(criterion, (fragment, isSeparator) => {
                if (!isSeparator) {
                    fragments.push(fragment);
                }
            });
            if (fragments.length > 0) {
                criterionRegexp = new RegExp(
                    "^(" + fragments.join("|") + ")",
                    "gim"
                );
            }
        }
        this.setState({
            criterion,
            criterionRegexp,
        });
        const parsedCriterion = this.parseCriterion(criterion);
        if (parsedCriterion.criterion === "") {
            callback([]);
            return Promise.resolve();
        }
        const extractorIdsForSuggestions = extractorRefs
            .filter((extractorRef) => !extractorRef.hideSuggestions)
            .map((extractorRef) => extractorRef.extractorId);
        if (extractorIdsForSuggestions.length === 0) {
            callback([]);
            console.warn("All extractor refs have hideSuggestions set to true");
            return Promise.resolve();
        }

        const orResults = dataViewIndex.getMatches(
            parsedCriterion.criterion,
            extractorIdsForSuggestions
        );
        const options = orResults
            .map((orResult) =>
                this.buildOptionsForOrResult(orResult, parsedCriterion)
            )
            .flat();
        callback(options);
        return Promise.resolve();
    };
    private onSelectItem = (item: FilterTypeaheadOption<ITEM>) => {
        const { onFilterSelected } = this.props;
        this.setState({ criterion: "", criterionRegexp: undefined });
        onFilterSelected(item.extractor.id, item.filter, item.excluded);
    };
    private parseCriterion(criterion: string): ParsedCriterion {
        let cleanCriterion: string;
        let excluded = false;
        if (criterion.length > 0) {
            const c0 = criterion.charAt(0);
            if (c0 === "-") {
                // exclude what is after "-"
                excluded = true;
                cleanCriterion = criterion.substring(1).trim();
            } else if (c0 === "+") {
                // include what is after "+"
                cleanCriterion = criterion.substring(1).trim();
            } else {
                cleanCriterion = criterion;
            }
        } else {
            cleanCriterion = criterion;
        }
        return {
            criterion: cleanCriterion,
            excluded,
        };
    }
}

interface ExtractorRef {
    extractorId: string;
    hideSuggestions?: boolean;
}

interface FilterTypeaheadProps<ITEM> {
    id: string;
    extractorRefs: Array<ExtractorRef>;
    queryResult: QueryResult<ITEM>;
    filter: SimpleAndFilter | null;
    hideResultCount?: boolean;
    dataViewIndex: DataViewIndex<ITEM>;
    onFilterSelected: (
        extractorId: string,
        filter: SimpleFieldFilter,
        excluded: boolean
    ) => void;
}

interface FilterTypeaheadState {
    criterion: string;
    criterionRegexp: RegExp | undefined;
}

export { type ExtractorRef, FilterTypeahead, type FilterTypeaheadProps };
